import { ENEMIES } from '../../data/enemies';
import { GROUND_Y, SIM_HZ } from '../../data/tuning';

/**
 * EN-02 Helicóptero de la ley (GDD §8.2, issue #84): hovers at a fixed point of the route and sweeps a 30 degree
 * searchlight. Staying 0.4 s in the cone turns it amber and 0.8 s fires one slow burst of three projectiles,
 * aimed at where the pilot is at that moment. The law is never shot down (GDD §3.4): it has no life and leaves
 * the screen with the scroll. Pure and deterministic.
 */
export const EN02 = ENEMIES['EN-02'];

export const AMBER_TICKS = Math.round(EN02.amberAfterSeconds * SIM_HZ);
export const BURST_TICKS = Math.round(EN02.burstAfterSeconds * SIM_HZ);
export const SWEEP_TICKS = Math.round(EN02.sweepSeconds * SIM_HZ);

export interface Projectile {
  x: number;
  y: number;
  /** Velocity in u per tick. */
  vx: number;
  vy: number;
  /** Ticks to wait before it leaves the helicopter (the shots of a burst are spaced). */
  delay: number;
}

export type Searchlight = 'idle' | 'amber';

export interface HelicopterState {
  /** Height still to descend before the searchlight works (the arrival from above). */
  arrivalLeft: number;
  /** Ticks since it became active. */
  ticks: number;
  /** Angle of the cone axis in degrees: 0 is straight down, positive towards +x. */
  angle: number;
  searchlight: Searchlight;
  /** Consecutive ticks the pilot has been inside the cone. */
  exposure: number;
  bursts: number;
  projectiles: Projectile[];
  /** Test fixture: the cone follows the pilot (not available in production). */
  locked: boolean;
}

export function createHelicopter(hoverY: number): HelicopterState {
  return {
    arrivalLeft: Math.max(0, EN02.arrivalFromY - hoverY),
    ticks: 0,
    angle: -EN02.sweepRangeDeg,
    searchlight: 'idle',
    exposure: 0,
    bursts: 0,
    projectiles: [],
    locked: false,
  };
}

/** Sweeps from -range to +range and back in SWEEP_TICKS: a triangle wave that starts at -range. */
export function sweepAngle(ticks: number): number {
  const phase = (ticks % SWEEP_TICKS) / SWEEP_TICKS;
  const wave = phase < 0.5 ? phase * 2 : 2 - phase * 2;
  return -EN02.sweepRangeDeg + 2 * EN02.sweepRangeDeg * wave;
}

export const sweepsCompleted = (ticks: number): number => Math.floor(ticks / SWEEP_TICKS);

const toDegrees = (radians: number) => (radians * 180) / Math.PI;

/** Direction from the helicopter to a point, as the angle of the cone axis would be (0 straight down). */
export function angleTo(from: { x: number; y: number }, to: { x: number; y: number }): number {
  return toDegrees(Math.atan2(to.x - from.x, from.y - to.y));
}

/**
 * Whether a point is inside the cone: a circular sector with its vertex at the helicopter, the aperture
 * around the axis and the reach down to the ground along the axis.
 */
export function inCone(from: { x: number; y: number }, axisDeg: number, point: { x: number; y: number }): boolean {
  if (point.y >= from.y) return false;
  const half = EN02.coneApertureDeg / 2;
  const offset = Math.abs(angleTo(from, point) - axisDeg);
  if (offset > half + 1e-9) return false;
  const reach = (from.y - GROUND_Y) / Math.max(Math.cos((axisDeg * Math.PI) / 180), 0.2);
  return Math.hypot(point.x - from.x, point.y - from.y) <= reach;
}

export interface HelicopterStep {
  /** Events to announce: the cone turned amber, a burst was fired. */
  amber: boolean;
  burst: boolean;
}

/**
 * One tick of an active helicopter at (x, y) with the pilot at `pilot`. A bribed pilot gets no exposure
 * (the law looks the other way, GDD §7; @gdd-pending). Returns what happened this tick.
 */
export function stepHelicopter(
  state: HelicopterState,
  at: { x: number; y: number },
  pilot: { x: number; y: number },
  bribed: boolean,
): HelicopterStep {
  const result: HelicopterStep = { amber: false, burst: false };
  state.ticks += 1;
  if (state.arrivalLeft > 0) return result;

  state.angle = state.locked ? angleTo(at, pilot) : sweepAngle(state.ticks);
  if (!bribed && inCone(at, state.angle, pilot)) state.exposure += 1;
  else state.exposure = 0;

  const before = state.searchlight;
  state.searchlight = state.exposure >= AMBER_TICKS ? 'amber' : 'idle';
  if (before === 'idle' && state.searchlight === 'amber') result.amber = true;

  if (state.exposure >= BURST_TICKS) {
    // The three projectiles aim at where the pilot is now; they do not chase it.
    const length = Math.hypot(pilot.x - at.x, pilot.y - at.y) || 1;
    const speed = EN02.projectileSpeed / SIM_HZ;
    for (let i = 0; i < EN02.projectilesPerBurst; i++) {
      state.projectiles.push({
        x: at.x,
        y: at.y,
        vx: ((pilot.x - at.x) / length) * speed,
        vy: ((pilot.y - at.y) / length) * speed,
        delay: i * EN02.projectileSpacingTicks,
      });
    }
    state.bursts += 1;
    state.exposure = 0;
    state.searchlight = 'idle';
    result.burst = true;
  }
  return result;
}

/** Moves the projectiles one tick and drops those outside the box (the screen with a margin). */
export function stepProjectiles(
  state: HelicopterState,
  box: { left: number; right: number; bottom: number; top: number },
): void {
  for (const projectile of state.projectiles) {
    if (projectile.delay > 0) {
      projectile.delay -= 1;
      continue;
    }
    projectile.x += projectile.vx;
    projectile.y += projectile.vy;
  }
  state.projectiles = state.projectiles.filter(
    (p) => p.delay > 0 || (p.x >= box.left && p.x <= box.right && p.y >= box.bottom && p.y <= box.top),
  );
}
